import { getDefaultRightPanelSize } from "@app/components/assistant/conversation/constant";
import type { AgentMessageWithStreaming } from "@app/components/assistant/conversation/types";
import { useActiveConversationId } from "@app/hooks/useActiveConversationId";
import { useHashParam } from "@app/hooks/useHashParams";
import type { ConversationSidePanelType } from "@app/types/conversation_side_panel";
import {
  AGENT_ACTIONS_SIDE_PANEL_TYPE,
  AGENT_SIDE_PANEL_TYPE,
  CREDITS_SIDE_PANEL_TYPE,
  FILE_PREVIEW_SIDE_PANEL_TYPE,
  FILES_SIDE_PANEL_TYPE,
  FULL_SCREEN_HASH_PARAM,
  INTERACTIVE_CONTENT_SIDE_PANEL_TYPE,
  PLAN_SIDE_PANEL_TYPE,
  SIDE_PANEL_HASH_PARAM,
  SIDE_PANEL_TYPE_HASH_PARAM,
  SKILL_SIDE_PANEL_TYPE,
  TOOL_SIDE_PANEL_TYPE,
} from "@app/types/conversation_side_panel";
import {
  assertNever,
  assertNeverAndIgnore,
} from "@app/types/shared/utils/assert_never";
import React, { useCallback, useEffect, useMemo } from "react";
import type { ImperativePanelHandle } from "react-resizable-panels";

type OpenPanelParams =
  | {
      type: "actions";
      messageId: string;
      actionId?: string;
    }
  | {
      type: "interactive_content";
      fileId: string;
      timestamp?: string;
    }
  | ({
      type: "file_preview";
    } & FilePreviewTarget)
  | {
      type: "files";
    }
  | {
      type: "credits";
    }
  | {
      type: "plan";
    }
  | {
      type: "skill";
      skillId: string;
      previewBatchId?: string;
    }
  | {
      type: "tool";
      toolId: string;
    }
  | {
      type: "agent";
      agentId: string;
      previewBatchId?: string;
    };

const FILE_PREVIEW_FILE_ID_PREFIX = "id:";

// Content fragments without a sandbox path are addressed by id instead.
export type FilePreviewTarget =
  | { kind: "path"; filePath: string }
  | { kind: "id"; fileId: string };

export function parseFilePreviewData(
  data: string | undefined
): FilePreviewTarget | null {
  if (!data) {
    return null;
  }

  return data.startsWith(FILE_PREVIEW_FILE_ID_PREFIX)
    ? { kind: "id", fileId: data.slice(FILE_PREVIEW_FILE_ID_PREFIX.length) }
    : { kind: "path", filePath: data };
}

export interface SuggestionPreviewTarget {
  entityId: string;
  // `undefined` when the panel previews no batch.
  batchId: string | undefined;
}

export function parseSuggestionPreviewData(
  data: string | undefined
): SuggestionPreviewTarget {
  const [entityId, batchId] = (data ?? "").split("@");
  return { entityId, batchId };
}

function filePreviewDataKey(target: FilePreviewTarget): string {
  switch (target.kind) {
    case "path":
      return target.filePath;
    case "id":
      return `${FILE_PREVIEW_FILE_ID_PREFIX}${target.fileId}`;
    default:
      return assertNever(target);
  }
}

// The `spid` hash value for a panel. Two panels are the same when type and key match.
function panelDataKey(params: OpenPanelParams): string {
  switch (params.type) {
    case AGENT_ACTIONS_SIDE_PANEL_TYPE:
      return params.actionId
        ? `${params.messageId}@${params.actionId}`
        : params.messageId;
    case INTERACTIVE_CONTENT_SIDE_PANEL_TYPE:
      return params.timestamp
        ? `${params.fileId}@${params.timestamp}`
        : params.fileId;
    case FILE_PREVIEW_SIDE_PANEL_TYPE:
      return filePreviewDataKey(params);
    case FILES_SIDE_PANEL_TYPE:
    case CREDITS_SIDE_PANEL_TYPE:
    case PLAN_SIDE_PANEL_TYPE:
      return params.type;
    case SKILL_SIDE_PANEL_TYPE:
      return params.previewBatchId
        ? `${params.skillId}@${params.previewBatchId}`
        : params.skillId;
    case TOOL_SIDE_PANEL_TYPE:
      return params.toolId;
    case AGENT_SIDE_PANEL_TYPE:
      return params.previewBatchId
        ? `${params.agentId}@${params.previewBatchId}`
        : params.agentId;
    default:
      return assertNever(params);
  }
}

// What makes two panels "the same view" for history purposes. Unlike the hash key, a Frame's
// timestamp is ignored: streaming refreshes the same Frame with a new timestamp, and that must
// update the shown panel rather than stack a copy of it.
function panelIdentityKey(params: OpenPanelParams): string {
  if (params.type === INTERACTIVE_CONTENT_SIDE_PANEL_TYPE) {
    return `${params.type}:${params.fileId}`;
  }
  return `${params.type}:${panelDataKey(params)}`;
}

// What goes in the history for a panel: a Frame is stored without its timestamp so going back
// loads its latest version rather than a streaming-era snapshot.
function panelHistoryEntry(params: OpenPanelParams): OpenPanelParams {
  if (params.type === INTERACTIVE_CONTENT_SIDE_PANEL_TYPE) {
    return { type: params.type, fileId: params.fileId };
  }
  return params;
}

// Inverse of panelDataKey, for panels restored from the URL hash (deep links, back/forward).
function panelParamsFromHash(
  type: ConversationSidePanelType,
  data: string | undefined
): OpenPanelParams | null {
  if (!type || !data) {
    return null;
  }
  switch (type) {
    case AGENT_ACTIONS_SIDE_PANEL_TYPE: {
      const [messageId, actionId] = data.split("@");
      return { type, messageId, actionId };
    }
    case INTERACTIVE_CONTENT_SIDE_PANEL_TYPE: {
      const [fileId, timestamp] = data.split("@");
      return { type, fileId, timestamp };
    }
    case FILE_PREVIEW_SIDE_PANEL_TYPE: {
      const target = parseFilePreviewData(data);
      return target ? { type, ...target } : null;
    }
    case FILES_SIDE_PANEL_TYPE:
    case CREDITS_SIDE_PANEL_TYPE:
    case PLAN_SIDE_PANEL_TYPE:
      return { type };
    case SKILL_SIDE_PANEL_TYPE: {
      const { entityId, batchId } = parseSuggestionPreviewData(data);
      return { type, skillId: entityId, previewBatchId: batchId };
    }
    case TOOL_SIDE_PANEL_TYPE:
      return { type, toolId: data };
    case AGENT_SIDE_PANEL_TYPE: {
      const { entityId, batchId } = parseSuggestionPreviewData(data);
      return { type, agentId: entityId, previewBatchId: batchId };
    }
    default:
      assertNeverAndIgnore(type);
      return null;
  }
}

const isSupportedPanelType = (
  type: string | undefined
): type is ConversationSidePanelType =>
  type === "actions" ||
  type === "credits" ||
  type === "interactive_content" ||
  type === "file_preview" ||
  type === "files" ||
  type === "plan" ||
  type === "skill" ||
  type === "tool" ||
  type === "agent";

interface ConversationSidePanelContextType {
  currentPanel: ConversationSidePanelType;
  // Preview affordances render nothing without a conversation to host the panel.
  hasConversation: boolean;
  // True between closePanel() and the end of the collapse transition. `currentPanel` keeps the
  // old value meanwhile so the panel content does not flicker; toggles read this to unselect
  // right away.
  isPanelClosing: boolean;
  openPanel: (params: OpenPanelParams) => void;
  togglePanel: (params: OpenPanelParams) => void;
  // Pops back to the previous panel in the history, or collapses when the history is empty.
  closePanel: () => void;
  // Removes panels of that type from the history, for content that no longer exists.
  removeFromPanelHistory: (type: ConversationSidePanelType) => void;
  onPanelClosed: () => void;
  setPanelRef: (ref: ImperativePanelHandle | null) => void;
  panelRef: React.MutableRefObject<ImperativePanelHandle | null>;
  setVirtuosoMsg: (msg: AgentMessageWithStreaming) => void;
  virtuosoMsg: AgentMessageWithStreaming | null;
  data: string | undefined;
}

// Past a few levels, going back one panel at a time stops matching what the user remembers, so
// the oldest entries are dropped.
const MAX_PANEL_HISTORY = 3;

export const ConversationSidePanelContext = React.createContext<
  ConversationSidePanelContextType | undefined
>(undefined);

export function useConversationSidePanelContext() {
  const context = React.useContext(ConversationSidePanelContext);
  if (!context) {
    throw new Error(
      "useConversationSidePanelContext must be used within a ConversationSidePanelProvider"
    );
  }

  return context;
}

// Separate from the main context so the setter stays out of consumers' reach.
const SidePanelConversationRegistrationContext = React.createContext<
  ((hasConversation: boolean) => void) | undefined
>(undefined);

// Called by whichever surface renders the panel content.
export function useRegisterSidePanelConversation(hasConversation: boolean) {
  const setHasConversation = React.useContext(
    SidePanelConversationRegistrationContext
  );

  useEffect(() => {
    setHasConversation?.(hasConversation);
    return () => setHasConversation?.(false);
  }, [hasConversation, setHasConversation]);
}

export function parseDataAsMessageIdAndActionId(data?: string): {
  messageId?: string;
  actionId?: string;
} {
  // data can be "messageId" or "messageId@actionId" for single-action view.
  // TODO: Clean up once inline activity is rolled out -- the single-action view
  // should fetch only the action it needs, not the full message.
  const [messageId, actionId] = data?.includes("@")
    ? data.split("@")
    : [data, undefined];

  return { messageId, actionId };
}

interface ConversationSidePanelProviderProps {
  children: React.ReactNode;
}

export function ConversationSidePanelProvider({
  children,
}: ConversationSidePanelProviderProps) {
  const [data, setData] = useHashParam(SIDE_PANEL_HASH_PARAM);
  const [currentPanel, setCurrentPanel] = useHashParam(
    SIDE_PANEL_TYPE_HASH_PARAM
  );
  const [, setFullScreenHash] = useHashParam(FULL_SCREEN_HASH_PARAM);
  const activeConversationId = useActiveConversationId();
  const previousConversationIdRef = React.useRef(activeConversationId);

  const panelRef = React.useRef<ImperativePanelHandle | null>(null);
  const [hasConversation, setHasConversation] = React.useState(false);
  const [isPanelClosing, setIsPanelClosing] = React.useState(false);
  const [virtuosoMsg, setVirtuosoMsg] =
    React.useState<AgentMessageWithStreaming | null>(null);
  const setPanelRef = useCallback(
    (ref: ImperativePanelHandle | null) => {
      panelRef.current = ref;
    },
    [panelRef]
  );

  // Panels shown before the current one and not closed since, most recent last. Closing pops
  // from here; opening a panel of another type pushes the current one (same-type panels replace
  // each other, so browsing tool steps or Frames does not pile up). Nothing renders from it, so a
  // ref is enough.
  const panelHistoryRef = React.useRef<OpenPanelParams[]>([]);
  const currentParamsRef = React.useRef<OpenPanelParams | null>(null);

  // This should be called once the closing animation is done (onTransitionEnd)
  // so you won't have content flickering. The whole side panel is gone at this point (X with
  // nothing underneath, divider drag, conversation switch), so the history goes with it.
  const onPanelClosed = useCallback(() => {
    setIsPanelClosing(false);
    currentParamsRef.current = null;
    panelHistoryRef.current = [];
    setData(undefined);
    setCurrentPanel(undefined);
  }, [setData, setCurrentPanel]);

  // Collapse without touching the history.
  const collapsePanel = useCallback(() => {
    currentParamsRef.current = null;
    if (panelRef && panelRef.current) {
      // Only flag a real collapse: on an already collapsed panel no transition runs, so
      // onPanelClosed would never clear the flag.
      if (!panelRef.current.isCollapsed()) {
        setIsPanelClosing(true);
      }
      panelRef.current.collapse();
    } else {
      // in case there is no ref found (agent builder preview), close the panel directly
      onPanelClosed();
    }
  }, [panelRef, onPanelClosed]);

  // Show a panel without touching the history: write the hash and expand. Re-expanding
  // imperatively covers a close→reopen race where `currentPanel` keeps the same value and the
  // container's expand effect would not re-run. No-op on mobile.
  const showPanel = useCallback(
    (params: OpenPanelParams) => {
      setIsPanelClosing(false);
      const previous = currentParamsRef.current;
      currentParamsRef.current = params;
      setCurrentPanel(params.type);
      setData(panelDataKey(params));
      // Only FrameRenderer can leave full screen, so a panel of another type would otherwise be
      // stranded at 100% with the nav bar hidden. Same-type shows keep it, so a refreshing Frame
      // does not drop out.
      if (previous && previous.type !== params.type) {
        setFullScreenHash(undefined);
      }
      panelRef.current?.expand(getDefaultRightPanelSize(params.type));
    },
    [setCurrentPanel, setData, setFullScreenHash]
  );

  const closePanel = useCallback(() => {
    const history = panelHistoryRef.current;
    const previous = history[history.length - 1];
    if (previous) {
      panelHistoryRef.current = history.slice(0, -1);
      showPanel(previous);
      return;
    }
    collapsePanel();
  }, [showPanel, collapsePanel]);

  const removeFromPanelHistory = useCallback(
    (type: ConversationSidePanelType) => {
      const history = panelHistoryRef.current;
      if (history.some((entry) => entry.type === type)) {
        panelHistoryRef.current = history.filter(
          (entry) => entry.type !== type
        );
      }
    },
    []
  );

  // Shared selection; `toggle` decides whether re-selecting the shown panel closes it. A panel
  // that is already closing reads as unselected, so re-selecting it reopens instead.
  const applyPanel = useCallback(
    (params: OpenPanelParams, { toggle }: { toggle: boolean }) => {
      const current = isPanelClosing ? null : currentParamsRef.current;
      const isShown =
        current !== null &&
        panelIdentityKey(current) === panelIdentityKey(params);

      if (isShown) {
        if (toggle) {
          closePanel();
        } else {
          showPanel(params);
        }
        return;
      }

      // The panel being shown never stays in the history, and only a change of panel type
      // stacks the current one.
      const targetKey = panelIdentityKey(params);
      let history = panelHistoryRef.current.filter(
        (entry) => panelIdentityKey(entry) !== targetKey
      );
      if (current && current.type !== params.type) {
        const entry = panelHistoryEntry(current);
        const entryKey = panelIdentityKey(entry);
        history = [
          ...history.filter((e) => panelIdentityKey(e) !== entryKey),
          entry,
        ].slice(-MAX_PANEL_HISTORY);
      }
      panelHistoryRef.current = history;
      showPanel(params);
    },
    [isPanelClosing, closePanel, showPanel]
  );

  // Idempotent open for programmatic callers: a toggle could mis-close during a close→reopen
  // transition where `currentPanel` still reads the old value.
  const openPanel = useCallback(
    (params: OpenPanelParams) => applyPanel(params, { toggle: false }),
    [applyPanel]
  );

  // Toggle for user-controlled buttons: re-selecting the shown panel closes it.
  const togglePanel = useCallback(
    (params: OpenPanelParams) => applyPanel(params, { toggle: true }),
    [applyPanel]
  );

  // Close the panel when switching conversations: the provider stays mounted
  // across navigation and useHashParam does not re-sync on pushState, so the
  // previous conversation's panel would otherwise stay open. Skips the initial
  // mount (deep links) and the null -> id transition (new conversation flow).
  useEffect(() => {
    const previousConversationId = previousConversationIdRef.current;
    previousConversationIdRef.current = activeConversationId;

    if (
      previousConversationId &&
      previousConversationId !== activeConversationId
    ) {
      // Exit full screen too, mirroring FrameRenderer's close button.
      setFullScreenHash(undefined);
      panelHistoryRef.current = [];
      collapsePanel();
    }
  }, [activeConversationId, collapsePanel, setFullScreenHash]);

  // Initialize panel state from URL hash parameters
  useEffect(() => {
    if (data && currentPanel) {
      setCurrentPanel(currentPanel);
      // Deep link or browser navigation: adopt the panel from the hash so it is the one we
      // consider shown. Our own opens already match, so the ref (and its Frame timestamp) is
      // kept in that case.
      if (isSupportedPanelType(currentPanel)) {
        const fromHash = panelParamsFromHash(currentPanel, data);
        const current = currentParamsRef.current;
        if (
          fromHash &&
          (!current || panelIdentityKey(current) !== panelIdentityKey(fromHash))
        ) {
          // The shown panel never stays in the history, same as when we open it ourselves.
          const shownKey = panelIdentityKey(fromHash);
          currentParamsRef.current = fromHash;
          panelHistoryRef.current = panelHistoryRef.current.filter(
            (entry) => panelIdentityKey(entry) !== shownKey
          );
        }
      }
    } else if (!data) {
      collapsePanel();
    }
  }, [data, currentPanel, setCurrentPanel, collapsePanel]);

  const value = useMemo(
    () => ({
      currentPanel: isSupportedPanelType(currentPanel)
        ? currentPanel
        : undefined,
      hasConversation,
      isPanelClosing,
      openPanel,
      togglePanel,
      closePanel,
      removeFromPanelHistory,
      onPanelClosed,
      setPanelRef,
      panelRef,
      setVirtuosoMsg,
      virtuosoMsg,
      data,
    }),
    [
      currentPanel,
      hasConversation,
      isPanelClosing,
      openPanel,
      togglePanel,
      closePanel,
      removeFromPanelHistory,
      onPanelClosed,
      setPanelRef,
      virtuosoMsg,
      data,
    ]
  );

  return (
    <SidePanelConversationRegistrationContext.Provider
      value={setHasConversation}
    >
      <ConversationSidePanelContext.Provider value={value}>
        {children}
      </ConversationSidePanelContext.Provider>
    </SidePanelConversationRegistrationContext.Provider>
  );
}
